export type UiLanguage = "en" | "tr";

/** Lightweight, dependency-free translation dictionary (no i18next — keeps this app's bundle tiny). */
export const translations = {
  en: {
    nav: {
      home: "Home",
      guide: "How it works",
      contact: "Contact us",
      settings: "Settings",
      users: "Users",
    },
    loading: {
      starting: "Starting Meeting Angelo Lite…",
      loadingMeetings: "Loading your meetings…",
      redirecting: "Redirecting…",
    },
    signIn: {
      brand: "Meeting Angelo Lite",
      tagline: "AI-powered meeting summaries and reports, right inside Microsoft Teams.",
      button: "Sign in with Microsoft",
      privacyNote: "Read-only access to your profile and calendar. Nothing is shared.",
      errorFallback: "Sign-in failed.",
      features: [
        {
          title: "AI meeting summaries",
          desc: "Decisions, action items and a clear recap generated automatically after every meeting.",
        },
        {
          title: "Zero setup for your team",
          desc: "Add it to a meeting and it just works — no admin policies, no PowerShell, no waiting.",
        },
        {
          title: "Your week at a glance",
          desc: "See your upcoming meetings and jump into the ones that matter, right inside Teams.",
        },
      ],
      howItWorks: "How it works",
      steps: [
        "Sign in with your Microsoft account.",
        "Add Meeting Angelo to any Teams meeting.",
        "Turn on transcription — the summary lands here when the meeting ends.",
      ],
      footer: "Meeting Angelo Lite · Want the full platform? Contact us at info@meetingangelo.com",
    },
    welcome: {
      greeting: "Welcome, {name}",
      defaultSubtitle: "Meeting Angelo Lite",
    },
    meetings: {
      title: "Upcoming meetings · next 7 days",
      empty: "No meetings scheduled for the next 7 days. Enjoy the focus time!",
      online: "Online",
      attendees: "attendees",
      join: "Join",
    },
    reports: {
      title: "Meeting reports",
      loading: "Loading your meeting reports…",
      empty: "No processed meetings yet. Once Meeting Angelo summarizes a meeting, the report shows up here automatically.",
      summaryReady: "Summary ready",
      failed: "Failed",
      processing: "Processing",
      noTranscript: "No transcript",
      participants: "participants",
    },
    reportView: {
      back: "← All reports",
      loadingSummary: "Loading AI summary…",
      notReady: "The AI summary for this meeting is not ready yet{status}. Check back in a few minutes.",
      statusSuffix: " (status: {status})",
      failedToLoad: "Failed to load report.",
    },
    upgrade: {
      title: "Unlock the full Meeting Angelo",
      body: "Automated recording across your whole organization, transcript analysis, participant scoring, CRM integration and multi-team dashboards — all powered by AI. Contact us at info@meetingangelo.com to get your Meeting Angelo web panel set up.",
      cta: "Open web panel",
    },
    howItWorksLink: "How it works",
    homeFooter: "Meeting Angelo Lite · AI meeting reports, right inside Teams.",
    guide: {
      title: "Get your meeting summary",
      subtitle: "Follow these steps once for each meeting you want Meeting Angelo Lite to summarize.",
      tip: "Your report appears automatically after the meeting ends. You can always return here when you need a quick refresher.",
    },
    onboarding: {
      steps: [
        {
          title: "What Meeting Angelo Lite does",
          body: "It records, transcribes and writes an AI summary of your Teams meetings — decisions, action items and a clear recap — with zero setup for your team.",
        },
        {
          title: "Step 1 — Add it to a meeting",
          body: 'Inside any Teams meeting, open "···" (More actions) in the meeting toolbar → "Add an app" → search "Meeting Angelo Lite" → Add. Do this once per meeting. No IT admin approval is needed — it works immediately.',
        },
        {
          title: "Step 2 — Start Teams transcription",
          body: 'After joining the meeting, open "···" (More actions) in the meeting toolbar → "Record and transcribe" → "Start transcription". This step is required: Meeting Angelo Lite can only create a summary after Teams produces a transcript.',
        },
        {
          title: "Step 3 — End the meeting and receive the summary",
          body: 'Once transcription is running, Meeting Angelo Lite tracks the meeting automatically. When the meeting ends, the AI summary appears here under "My reports" and is emailed out — you do not need to start or stop anything else.',
        },
        {
          title: "Step 4 — Choose who gets the summary",
          body: 'By default, only the person who added the app receives the e-mail. Open Settings → "Who receives the AI summary" (or the panel inside the meeting itself) to send it to everyone in the meeting instead.',
        },
        {
          title: "Licensing",
          body: "Your organization needs an active Meeting Angelo Lite subscription to keep using the app. Check the current plan any time in Settings → License — that's also where an admin buys or activates it.",
        },
        {
          title: "You're an Admin",
          body: "You were made Admin automatically because you're the first person from your organization to sign in. Use Settings for organization-wide preferences, and the Users page to grant or change access for your colleagues.",
        },
      ],
      back: "Back",
      next: "Next",
      finish: "Got it — take me to my reports",
      skip: "Skip",
    },
    subscriptionGate: {
      badge: "Teams subscription",
      languageLabel: "Purchase page language",
      title: "Meeting Angelo Lite is a paid app",
      subtitle: "This organization does not have an active subscription yet. Purchase the Meeting Angelo Lite plan from the Teams Store to unlock AI meeting summaries.",
      note: "Every plan (monthly or annual) includes a monthly meeting quota shared across your whole organization — once purchased, your admin can hand out a personal monthly limit to each person from Settings, or leave everyone drawing from the shared pool.",
      features: [
        { title: "AI meeting summaries", body: "Turn meeting conversations into a clear recap with decisions and action items." },
        { title: "Automatic delivery", body: "Your summary is prepared after the meeting and delivered without manual follow-up." },
        { title: "Meeting-level control", body: "Add the app only to the meetings you want to track and keep ownership in Teams." },
        { title: "Built for your organization", body: "One subscription gives your organization a shared monthly meeting credit pool." },
      ],
      purchaseInfo: "Select the plan that fits your organization in the secure Teams purchase window. Your subscription is activated automatically after checkout.",
      notConfiguredBadge: "Billing setup in progress",
      notConfiguredBody: "The publisher hasn't finished the Marketplace billing configuration yet. Please try again shortly or contact info@meetingangelo.com.",
      pendingBadge: "Purchase received — activating…",
      pendingBody: 'We detected a pending purchase. This usually activates within a minute; use "Refresh status" below once it\'s done.',
      suspendedBadge: "Subscription suspended",
      suspendedBody: "Billing for this subscription needs attention. Contact info@meetingangelo.com or your admin center billing page.",
      cancelled: "The previous subscription was cancelled. Purchase a new plan to continue.",
      buy: "Buy Meeting Angelo Lite",
      refresh: "Refresh status",
      footer: "Meeting Angelo Lite · offer: {offerId}",
    },
    settings: {
      title: "Settings",
      errors: {
        loadOrgSettings: "Could not load organization settings.",
        saveChange: "Could not save the change.",
        loadOrgCredits: "Could not load organization credits.",
        saveLimit: "Could not save the limit.",
        addRecipient: "Could not add the recipient.",
        removeRecipient: "Could not remove the recipient.",
      },
      language: {
        sectionTitle: "App language",
        label: "Interface language",
      },
      summaryLanguage: {
        sectionTitle: "AI summary language",
        description: "Choose which language your AI meeting summaries are written in. This only changes future summaries — already-generated ones don't change.",
        loading: "Loading languages…",
        saved: "Saved.",
        error: "Could not save your summary language.",
      },
      license: {
        title: "License",
        noPlan: "No plan on file for this organization yet.",
        plan: "Plan: {planId}",
        renewsEnds: " · renews/ends {date}",
        trial: "Trial",
        buyUpgrade: "Buy / upgrade",
        refreshStatus: "Refresh status",
        statusSubscribed: "Active",
        statusPending: "Pending approval",
        statusSuspended: "Suspended",
        statusCancelled: "Cancelled",
        statusNone: "No license",
      },
      myCredits: {
        title: "My meeting credits",
        loading: "Loading your meeting credits…",
        used: "{used} / {limit} meetings used",
        usedNoLimit: "{used} meetings used",
        personalLimit: "Personal limit",
        sharedPool: "Shared pool",
        withLimit: "Your admin assigned you {limit} meetings/month. {remaining} left this month.",
        withoutLimit: "You're drawing from your organization's shared pool ({remaining} of {total} left this month).",
      },
      orgCredits: {
        title: "Organization meeting credits (admin only)",
        loading: "Loading organization credits…",
        currentPlan: "Current plan",
        usedThisMonth: "{used} / {total} used this month",
        allocatedSummary: "{allocated} assigned to specific people, {unallocated} still shared/unassigned.",
        perPersonLimit: "Per-person monthly limit",
        perPersonNote: '0 means "shared pool" — that person can use meetings as long as the organization pool above has capacity. Set a number to give them a personal cap instead.',
        used: "used",
        left: "left",
        save: "Save",
      },
      permissions: {
        title: "Permissions",
        signedInAs: "Signed in as {who}",
        requestsIntro: "Meeting Angelo Lite only ever requests:",
        userRead: "User.Read — your basic profile",
        calendarsRead: "Calendars.Read — your upcoming meetings, to show your agenda",
        perMeeting: "Per-meeting access — transcript access only for the individual meetings you add the app to (Teams resource-specific consent), never organization-wide",
        privacyPolicy: "Privacy policy",
        termsOfUse: "Terms of use",
      },
      recipients: {
        title: "Who receives the AI summary",
        empty: 'This appears automatically once you add the app to a meeting and it starts tracking the transcript. Add Meeting Angelo Lite to a meeting to configure who gets the summary.',
        noneTracked: "None of your recent meetings are currently tracked by Meeting Angelo Lite.",
        sentToEveryone: "Sent to everyone in the meeting",
        sentToYou: "Sent only to you",
        hint: "This is also available directly inside each meeting's side panel while it's active.",
      },
      orgDefaults: {
        title: "Organization defaults (admin only)",
        loading: "Loading organization settings…",
        summaryTemplate: "Summary template",
        summaryTemplateDesc: "Applies to every new AI summary for your organization.",
        deliveryMode: "Default summary delivery",
        deliveryModeDesc: "Who gets the summary when nobody overrides it for that meeting.",
        predefinedRecipients: "Predefined recipients",
        noPredefined: "No predefined recipients yet.",
        namePlaceholder: "Name",
        emailPlaceholder: "Email",
        add: "Add",
        remove: "Remove",
        templates: {
          0: "Detailed Template",
          1: "Standard Template",
          2: "Action Items Only",
          3: "Technical Review",
          4: "Executive Brief",
          5: "Customer Meeting",
          6: "Sales Call",
          7: "Project Kickoff",
          8: "Quick Recap",
          9: "Classic Detailed",
          10: "HR Interview",
          11: "1:1 Meeting",
          12: "Marketing Sync",
          13: "Finance Review",
          14: "Training Session",
        },
        deliveryModes: {
          0: "Send to All Users",
          1: "Send to Internal Users Only",
          2: "Send to Predefined Users Only",
          3: "Send to Organizer Only",
          4: "Send to Recording Initiator Only",
        },
      },
      footer: "Meeting Angelo Lite · offer: {offerId}",
    },
    users: {
      title: "Users & roles",
      subtitle: "Everyone who has signed in to Meeting Angelo Lite for your organization. The first person to sign in was made Admin automatically — from here they can promote or restrict anyone else.",
      people: "People",
      loading: "Loading users…",
      loadError: "Could not load users.",
      updateRoleError: "Could not update the role.",
      you: "You",
      cantChangeSelf: "You can't change your own role here, to avoid locking yourself out.",
      footer: "Meeting Angelo Lite · roles apply across the whole app, not just Teams Lite.",
      roles: {
        1: "Super admin",
        2: "Admin",
        3: "User",
        4: "Report viewer",
        5: "Manager",
        6: "Auditor",
        7: "Billing",
      },
    },
    contact: {
      title: "Contact us",
      subtitle: "Tell us how we can help. Your request is sent directly to the Meeting Angelo team.",
      subject: "Subject",
      message: "Message",
      replyNote: "The Meeting Angelo team will reply from info@meetingangelo.com.",
      send: "Send request",
      success: "Thanks. Your request has been sent.",
      error: "Your request could not be sent. Please try again.",
    },
    meetingPanel: {
      thisMeeting: "This meeting",
      meetingInProgress: "Meeting in progress",
      trackedReady: "Tracked by Meeting Angelo · summary {state}",
      ready: "ready",
      pending: "pending",
      trackedWaitingSummary: "Tracked · summary arrives when transcription ends",
      trackedWaitingTranscript: "Tracked · waiting for transcript",
      activating: "Activating Meeting Angelo tracking…",
      turnOnTranscription: "Turn on transcription in this meeting and the AI summary will appear here automatically after it ends.",
      emailToEveryone: "Email the summary to everyone",
      sentToEveryone: "All meeting participants will receive the AI summary.",
      sentToYou: "Only you (who added the app) will receive the AI summary.",
      loadingSummary: "Loading AI summary…",
      aiSummary: "AI summary",
      recentReports: "Recent meeting reports",
      wantMore: "Want more from Meeting Angelo?",
      wantMoreBody: "Automated recording across your organization, participant scoring, CRM integration and advanced reports come with the Meeting Angelo web panel. Contact us at info@meetingangelo.com to get your web panel set up.",
      openFullApp: "Open full app",
      creditErrors: {
        noSubTitle: "No active plan",
        noSubBody: "This organization doesn't have an active Meeting Angelo Lite subscription yet. Purchase a plan to start tracking meetings.",
        noSubCta: "Buy Meeting Angelo Lite",
        poolTitle: "Monthly meeting limit reached",
        poolBody: "Your organization has used all {total} meeting credits included in its plan this month. Ask your admin to upgrade the plan.",
        poolCta: "View organization usage",
        userTitle: "Your monthly meeting limit reached",
        userBody: "You've used all the meetings your admin assigned you this month. Ask your admin to raise your personal limit.",
        userCta: "View my limit",
      },
    },
  },
  tr: {
    nav: {
      home: "Ana Sayfa",
      guide: "Nasıl çalışır",
      contact: "Bizimle iletişime geçin",
      settings: "Ayarlar",
      users: "Kullanıcılar",
    },
    loading: {
      starting: "Meeting Angelo Lite başlatılıyor…",
      loadingMeetings: "Toplantılarınız yükleniyor…",
      redirecting: "Yönlendiriliyor…",
    },
    signIn: {
      brand: "Meeting Angelo Lite",
      tagline: "Microsoft Teams içinde, yapay zeka destekli toplantı özetleri ve raporları.",
      button: "Microsoft ile giriş yap",
      privacyNote: "Profilinize ve takviminize yalnızca okuma erişimi. Hiçbir şey paylaşılmaz.",
      errorFallback: "Giriş başarısız oldu.",
      features: [
        {
          title: "Yapay zeka toplantı özetleri",
          desc: "Her toplantıdan sonra kararlar, aksiyon maddeleri ve net bir özet otomatik olarak oluşturulur.",
        },
        {
          title: "Ekibiniz için kurulum gerektirmez",
          desc: "Bir toplantıya ekleyin, hemen çalışsın — admin politikası, PowerShell veya bekleme yok.",
        },
        {
          title: "Haftanıza tek bakışta hakim olun",
          desc: "Yaklaşan toplantılarınızı görün ve önemli olanlara doğrudan Teams içinden katılın.",
        },
      ],
      howItWorks: "Nasıl çalışır",
      steps: [
        "Microsoft hesabınızla giriş yapın.",
        "Meeting Angelo'yu herhangi bir Teams toplantısına ekleyin.",
        "Transkripti açın — toplantı bittiğinde özet burada belirir.",
      ],
      footer: "Meeting Angelo Lite · Tüm platformu mu istiyorsunuz? info@meetingangelo.com adresinden bize ulaşın",
    },
    welcome: {
      greeting: "Hoş geldiniz, {name}",
      defaultSubtitle: "Meeting Angelo Lite",
    },
    meetings: {
      title: "Yaklaşan toplantılar · sonraki 7 gün",
      empty: "Önümüzdeki 7 gün için planlanmış toplantı yok. Odaklanma zamanının tadını çıkarın!",
      online: "Çevrimiçi",
      attendees: "katılımcı",
      join: "Katıl",
    },
    reports: {
      title: "Toplantı raporları",
      loading: "Toplantı raporlarınız yükleniyor…",
      empty: "Henüz işlenmiş bir toplantı yok. Meeting Angelo bir toplantıyı özetlediğinde rapor burada otomatik olarak görünür.",
      summaryReady: "Özet hazır",
      failed: "Başarısız",
      processing: "İşleniyor",
      noTranscript: "Transkript yok",
      participants: "katılımcı",
    },
    reportView: {
      back: "← Tüm raporlar",
      loadingSummary: "Yapay zeka özeti yükleniyor…",
      notReady: "Bu toplantının yapay zeka özeti henüz hazır değil{status}. Birkaç dakika sonra tekrar kontrol edin.",
      statusSuffix: " (durum: {status})",
      failedToLoad: "Rapor yüklenemedi.",
    },
    upgrade: {
      title: "Meeting Angelo'nun tamamının kilidini açın",
      body: "Tüm kuruluşunuz genelinde otomatik kayıt, transkript analizi, katılımcı puanlama, CRM entegrasyonu ve çok takımlı panolar — hepsi yapay zeka ile. Meeting Angelo web panelinizi kurmak için info@meetingangelo.com adresinden bize ulaşın.",
      cta: "Web panelini aç",
    },
    howItWorksLink: "Nasıl çalışır",
    homeFooter: "Meeting Angelo Lite · Teams içinde yapay zeka toplantı raporları.",
    guide: {
      title: "Toplantı özetinizi alın",
      subtitle: "Meeting Angelo Lite'ın özet hazırlamasını istediğiniz her toplantı için bu adımları izleyin.",
      tip: "Raporunuz toplantı bittikten sonra otomatik olarak görünür. Hızlı bir hatırlatmaya ihtiyaç duyduğunuzda bu sayfaya her zaman dönebilirsiniz.",
    },
    onboarding: {
      steps: [
        {
          title: "Meeting Angelo Lite ne yapar",
          body: "Teams toplantılarınızı kaydeder, deşifre eder ve kararlar, aksiyon maddeleri ve net bir özet içeren bir yapay zeka özeti yazar — ekibiniz için hiçbir kurulum gerekmez.",
        },
        {
          title: "1. Adım — Bir toplantıya ekleyin",
          body: 'Herhangi bir Teams toplantısında toplantı araç çubuğundaki "···" (Diğer eylemler) → "Uygulama ekle" → "Meeting Angelo Lite" araması → Ekle. Bunu toplantı başına bir kez yapın. BT admin onayı gerekmez — hemen çalışır.',
        },
        {
          title: "2. Adım — Teams transkriptini başlatın",
          body: 'Toplantıya katıldıktan sonra toplantı araç çubuğundaki "···" (Diğer eylemler) → "Kaydet ve deşifre et" → "Deşifreyi başlat" seçeneklerini açın. Bu adım zorunludur: Meeting Angelo Lite, Teams bir transkript oluşturduktan sonra özet hazırlayabilir.',
        },
        {
          title: "3. Adım — Toplantıyı bitirin ve özeti alın",
          body: 'Transkript çalışmaya başladıktan sonra Meeting Angelo Lite toplantıyı otomatik olarak izler. Toplantı bittiğinde yapay zeka özeti burada "Raporlarım" altında görünür ve e-posta ile gönderilir — başka bir işlem başlatmanız veya durdurmanız gerekmez.',
        },
        {
          title: "4. Adım — Özeti kimlerin alacağını seçin",
          body: 'Varsayılan olarak yalnızca uygulamayı ekleyen kişi e-postayı alır. Bunun yerine toplantıdaki herkese göndermek için Ayarlar → "Yapay zeka özetini kim alır" (veya toplantı içindeki panel) bölümünü açın.',
        },
        {
          title: "Lisanslama",
          body: "Kuruluşunuzun uygulamayı kullanmaya devam edebilmesi için aktif bir Meeting Angelo Lite aboneliğine ihtiyacı var. Güncel planı her zaman Ayarlar → Lisans bölümünden kontrol edebilirsiniz — bir admin de buradan satın alabilir veya etkinleştirebilir.",
        },
        {
          title: "Admin'siniz",
          body: "Kuruluşunuzdan giriş yapan ilk kişi olduğunuz için otomatik olarak Admin yapıldınız. Kuruluş çapındaki tercihler için Ayarlar'ı, çalışma arkadaşlarınızın erişimini vermek veya değiştirmek için Kullanıcılar sayfasını kullanın.",
        },
      ],
      back: "Geri",
      next: "İleri",
      finish: "Anladım — raporlarıma götür",
      skip: "Atla",
    },
    subscriptionGate: {
      badge: "Teams aboneliği",
      languageLabel: "Satın alma sayfası dili",
      title: "Meeting Angelo Lite ücretli bir uygulamadır",
      subtitle: "Bu kuruluşun henüz aktif bir aboneliği yok. Yapay zeka toplantı özetlerinin kilidini açmak için Teams Store'dan Meeting Angelo Lite planını satın alın.",
      note: "Her plan (aylık veya yıllık) tüm kuruluşunuz genelinde paylaşılan aylık bir toplantı kotası içerir — satın alındıktan sonra admininiz Ayarlar'dan kişilere kişisel aylık limit verebilir veya herkesin paylaşılan havuzdan yararlanmasına izin verebilir.",
      features: [
        { title: "Yapay zeka toplantı özetleri", body: "Toplantı konuşmalarını kararlar ve aksiyon maddeleriyle anlaşılır bir özete dönüştürün." },
        { title: "Otomatik teslimat", body: "Özetiniz toplantıdan sonra hazırlanır ve manuel takip gerektirmeden ulaştırılır." },
        { title: "Toplantı bazında kontrol", body: "Uygulamayı yalnızca takip etmek istediğiniz toplantılara ekleyin ve yönetimi Teams'te tutun." },
        { title: "Kuruluşunuz için tasarlandı", body: "Tek abonelik, kuruluşunuz için paylaşılan aylık toplantı kredisi havuzu sağlar." },
      ],
      purchaseInfo: "Kuruluşunuza uygun planı güvenli Teams satın alma penceresinden seçin. Aboneliğiniz ödeme sonrasında otomatik olarak etkinleştirilir.",
      notConfiguredBadge: "Faturalandırma kurulumu devam ediyor",
      notConfiguredBody: "Yayıncı henüz Marketplace faturalandırma yapılandırmasını tamamlamadı. Lütfen kısa süre sonra tekrar deneyin veya info@meetingangelo.com adresinden bize ulaşın.",
      pendingBadge: "Satın alma alındı — etkinleştiriliyor…",
      pendingBody: 'Bekleyen bir satın alma tespit ettik. Bu genellikle bir dakika içinde etkinleşir; tamamlandığında aşağıdaki "Durumu yenile" seçeneğini kullanın.',
      suspendedBadge: "Abonelik askıya alındı",
      suspendedBody: "Bu aboneliğin faturalandırması ilgi gerektiriyor. info@meetingangelo.com adresinden bize ulaşın veya admin merkezi faturalandırma sayfanızı kontrol edin.",
      cancelled: "Önceki abonelik iptal edildi. Devam etmek için yeni bir plan satın alın.",
      buy: "Meeting Angelo Lite satın al",
      refresh: "Durumu yenile",
      footer: "Meeting Angelo Lite · teklif: {offerId}",
    },
    settings: {
      title: "Ayarlar",
      errors: {
        loadOrgSettings: "Kuruluş ayarları yüklenemedi.",
        saveChange: "Değişiklik kaydedilemedi.",
        loadOrgCredits: "Kuruluş kredileri yüklenemedi.",
        saveLimit: "Limit kaydedilemedi.",
        addRecipient: "Alıcı eklenemedi.",
        removeRecipient: "Alıcı kaldırılamadı.",
      },
      language: {
        sectionTitle: "Uygulama dili",
        label: "Arayüz dili",
      },
      summaryLanguage: {
        sectionTitle: "Yapay zeka özeti dili",
        description: "Yapay zeka toplantı özetlerinizin hangi dilde yazılacağını seçin. Bu yalnızca gelecekteki özetleri değiştirir — zaten oluşturulmuş olanlar değişmez.",
        loading: "Diller yükleniyor…",
        saved: "Kaydedildi.",
        error: "Özet diliniz kaydedilemedi.",
      },
      license: {
        title: "Lisans",
        noPlan: "Bu kuruluş için henüz kayıtlı bir plan yok.",
        plan: "Plan: {planId}",
        renewsEnds: " · yenileme/bitiş {date}",
        trial: "Deneme",
        buyUpgrade: "Satın al / yükselt",
        refreshStatus: "Durumu yenile",
        statusSubscribed: "Aktif",
        statusPending: "Onay bekliyor",
        statusSuspended: "Askıya alındı",
        statusCancelled: "İptal edildi",
        statusNone: "Lisans yok",
      },
      myCredits: {
        title: "Toplantı kredilerim",
        loading: "Toplantı krediniz yükleniyor…",
        used: "{used} / {limit} toplantı kullanıldı",
        usedNoLimit: "{used} toplantı kullanıldı",
        personalLimit: "Kişisel limit",
        sharedPool: "Paylaşılan havuz",
        withLimit: "Admininiz size aylık {limit} toplantı atadı. Bu ay {remaining} tane kaldı.",
        withoutLimit: "Kuruluşunuzun paylaşılan havuzundan yararlanıyorsunuz (bu ay {total} tanenin {remaining} tanesi kaldı).",
      },
      orgCredits: {
        title: "Kuruluş toplantı kredileri (yalnızca admin)",
        loading: "Kuruluş kredileri yükleniyor…",
        currentPlan: "Güncel plan",
        usedThisMonth: "Bu ay {used} / {total} kullanıldı",
        allocatedSummary: "{allocated} tanesi belirli kişilere atandı, {unallocated} tanesi hâlâ paylaşılan/atanmamış.",
        perPersonLimit: "Kişi başı aylık limit",
        perPersonNote: '0, "paylaşılan havuz" anlamına gelir — o kişi yukarıdaki kuruluş havuzunda kapasite olduğu sürece toplantı kullanabilir. Kişisel bir üst limit vermek için bir sayı girin.',
        used: "kullanıldı",
        left: "kaldı",
        save: "Kaydet",
      },
      permissions: {
        title: "İzinler",
        signedInAs: "{who} olarak giriş yapıldı",
        requestsIntro: "Meeting Angelo Lite yalnızca şunları ister:",
        userRead: "User.Read — temel profiliniz",
        calendarsRead: "Calendars.Read — gündeminizi göstermek için yaklaşan toplantılarınız",
        perMeeting: "Toplantı başına erişim — yalnızca uygulamayı eklediğiniz bireysel toplantılar için transkript erişimi (Teams kaynağa özel izin), asla kuruluş genelinde değil",
        privacyPolicy: "Gizlilik politikası",
        termsOfUse: "Kullanım şartları",
      },
      recipients: {
        title: "Yapay zeka özetini kim alır",
        empty: "Bu, uygulamayı bir toplantıya ekleyip transkripti izlemeye başladığınızda otomatik olarak görünür. Özeti kimin alacağını yapılandırmak için Meeting Angelo Lite'ı bir toplantıya ekleyin.",
        noneTracked: "Son toplantılarınızdan hiçbiri şu anda Meeting Angelo Lite tarafından izlenmiyor.",
        sentToEveryone: "Toplantıdaki herkese gönderiliyor",
        sentToYou: "Yalnızca size gönderiliyor",
        hint: "Bu ayar, aktifken her toplantının yan panelinden de doğrudan kullanılabilir.",
      },
      orgDefaults: {
        title: "Kuruluş varsayılanları (yalnızca admin)",
        loading: "Kuruluş ayarları yükleniyor…",
        summaryTemplate: "Özet şablonu",
        summaryTemplateDesc: "Kuruluşunuz için her yeni yapay zeka özetine uygulanır.",
        deliveryMode: "Varsayılan özet teslimatı",
        deliveryModeDesc: "Kimse o toplantı için geçersiz kılmadığında özeti kim alır.",
        predefinedRecipients: "Önceden tanımlı alıcılar",
        noPredefined: "Henüz önceden tanımlı alıcı yok.",
        namePlaceholder: "Ad",
        emailPlaceholder: "E-posta",
        add: "Ekle",
        remove: "Kaldır",
        templates: {
          0: "Detaylı Şablon",
          1: "Standart Şablon",
          2: "Yalnızca Aksiyon Maddeleri",
          3: "Teknik İnceleme",
          4: "Yönetici Özeti",
          5: "Müşteri Toplantısı",
          6: "Satış Görüşmesi",
          7: "Proje Başlangıcı",
          8: "Hızlı Özet",
          9: "Klasik Detaylı",
          10: "İK Mülakatı",
          11: "Birebir Görüşme",
          12: "Pazarlama Senkronizasyonu",
          13: "Finans İncelemesi",
          14: "Eğitim Oturumu",
        },
        deliveryModes: {
          0: "Tüm Kullanıcılara Gönder",
          1: "Yalnızca İç Kullanıcılara Gönder",
          2: "Yalnızca Önceden Tanımlı Kullanıcılara Gönder",
          3: "Yalnızca Organizatöre Gönder",
          4: "Yalnızca Kaydı Başlatana Gönder",
        },
      },
      footer: "Meeting Angelo Lite · teklif: {offerId}",
    },
    users: {
      title: "Kullanıcılar ve roller",
      subtitle: "Kuruluşunuz için Meeting Angelo Lite'a giriş yapan herkes. Giriş yapan ilk kişi otomatik olarak Admin yapıldı — buradan başkalarını yükseltebilir veya kısıtlayabilirler.",
      people: "Kişiler",
      loading: "Kullanıcılar yükleniyor…",
      loadError: "Kullanıcılar yüklenemedi.",
      updateRoleError: "Rol güncellenemedi.",
      you: "Siz",
      cantChangeSelf: "Kendinizi kilitlemekten kaçınmak için kendi rolünüzü burada değiştiremezsiniz.",
      footer: "Meeting Angelo Lite · roller yalnızca Teams Lite'ta değil, uygulamanın tamamında geçerlidir.",
      roles: {
        1: "Süper admin",
        2: "Admin",
        3: "Kullanıcı",
        4: "Rapor görüntüleyici",
        5: "Yönetici",
        6: "Denetçi",
        7: "Fatura",
      },
    },
    contact: {
      title: "Bizimle iletişime geçin",
      subtitle: "Size nasıl yardımcı olabileceğimizi yazın. Talebiniz doğrudan Meeting Angelo ekibine iletilir.",
      subject: "Konu",
      message: "Mesaj",
      replyNote: "Meeting Angelo ekibi size info@meetingangelo.com adresinden dönüş yapacaktır.",
      send: "Talebi gönder",
      success: "Teşekkürler. Talebiniz gönderildi.",
      error: "Talebiniz gönderilemedi. Lütfen tekrar deneyin.",
    },
    meetingPanel: {
      thisMeeting: "Bu toplantı",
      meetingInProgress: "Toplantı devam ediyor",
      trackedReady: "Meeting Angelo tarafından izleniyor · özet {state}",
      ready: "hazır",
      pending: "bekliyor",
      trackedWaitingSummary: "İzleniyor · transkript bittiğinde özet gelir",
      trackedWaitingTranscript: "İzleniyor · transkript bekleniyor",
      activating: "Meeting Angelo izleme etkinleştiriliyor…",
      turnOnTranscription: "Bu toplantıda transkripti açın, toplantı bittiğinde yapay zeka özeti otomatik olarak burada görünür.",
      emailToEveryone: "Özeti herkese e-posta ile gönder",
      sentToEveryone: "Tüm toplantı katılımcıları yapay zeka özetini alacak.",
      sentToYou: "Yalnızca siz (uygulamayı ekleyen kişi) yapay zeka özetini alacaksınız.",
      loadingSummary: "Yapay zeka özeti yükleniyor…",
      aiSummary: "Yapay zeka özeti",
      recentReports: "Son toplantı raporları",
      wantMore: "Meeting Angelo'dan daha fazlasını mı istiyorsunuz?",
      wantMoreBody: "Kuruluşunuz genelinde otomatik kayıt, katılımcı puanlama, CRM entegrasyonu ve gelişmiş raporlar Meeting Angelo web paneliyle gelir. Web panelinizi kurmak için info@meetingangelo.com adresinden bize ulaşın.",
      openFullApp: "Tam uygulamayı aç",
      creditErrors: {
        noSubTitle: "Aktif plan yok",
        noSubBody: "Bu kuruluşun henüz aktif bir Meeting Angelo Lite aboneliği yok. Toplantıları izlemeye başlamak için bir plan satın alın.",
        noSubCta: "Meeting Angelo Lite satın al",
        poolTitle: "Aylık toplantı limitine ulaşıldı",
        poolBody: "Kuruluşunuz bu ay planına dahil olan {total} toplantı kredisinin tamamını kullandı. Admininizden planı yükseltmesini isteyin.",
        poolCta: "Kuruluş kullanımını görüntüle",
        userTitle: "Aylık toplantı limitinize ulaşıldı",
        userBody: "Admininizin size bu ay atadığı toplantıların tamamını kullandınız. Admininizden kişisel limitinizi artırmasını isteyin.",
        userCta: "Limitimi görüntüle",
      },
    },
  },
} as const;

/** Interpolates {placeholders} in a translation string with the given values. */
export function format(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => String(values[key] ?? ""));
}
